'use client';

import { useEffect } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { useSelector } from 'react-redux';
import NProgress from 'nprogress';

// Configure NProgress for super smooth transitions
NProgress.configure({ 
  showSpinner: false,
  trickle: true,
  trickleSpeed: 50,
  minimum: 0.02,
  easing: 'linear',
  speed: 200,
  parent: 'body'
});

const ProgressProvider = ({ children }) => {
  const router = useRouter();
  const pathname = usePathname();
  const theme = useSelector((state) => state.ui.theme);

  // Override router methods to show progress
  useEffect(() => {
    const originalPush = router.push;
    const originalReplace = router.replace;
    const originalBack = router.back;
    const originalForward = router.forward;

    // Override router methods with smooth delay
    router.push = (...args) => {
      setTimeout(() => NProgress.start(), 50);
      return originalPush.apply(router, args);
    };

    router.replace = (...args) => {
      setTimeout(() => NProgress.start(), 50);
      return originalReplace.apply(router, args);
    };

    router.back = (...args) => {
      setTimeout(() => NProgress.start(), 50);
      return originalBack.apply(router, args);
    };

    router.forward = (...args) => {
      setTimeout(() => NProgress.start(), 50);
      return originalForward.apply(router, args);
    };

    // Handle browser back/forward buttons
    const handlePopState = () => {
      setTimeout(() => NProgress.start(), 50);
    };

    window.addEventListener('popstate', handlePopState);

    // Cleanup
    return () => {
      router.push = originalPush;
      router.replace = originalReplace;
      router.back = originalBack;
      router.forward = originalForward;
      window.removeEventListener('popstate', handlePopState);
    };
  }, [router]);

  // Complete progress when route changes
  useEffect(() => {
    NProgress.done();
  }, [pathname]);

  // Handle theme changes - temporarily disable transitions
  useEffect(() => {
    if (typeof window !== 'undefined') {
      document.documentElement.classList.add('disable-transitions');
      
      const timeout = setTimeout(() => {
        document.documentElement.classList.remove('disable-transitions');
      }, 50);
      
      return () => clearTimeout(timeout);
    }
  }, [theme]);

  return children;
};

export default ProgressProvider;
